import React from 'react';
import Image from 'next/image';

import { formatCurrency } from '~/utils/number';

interface ProgramSelectProps {
  cost: number;
  image: string;
  onQuantityUpdate: (op: string, amount: number) => void;
  onUnitAmountChange: (unitTotal: number) => void;
  step: number;
  title: string;
  unitDescription: string;
}

export function ProgramSelect({
  title,
  cost,
  unitDescription,
  step,
  onQuantityUpdate,
  image,
  onUnitAmountChange,
}: ProgramSelectProps) {
  const [total, setTotal] = React.useState(0);

  const handleIncreaseTotal = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();

      const newTotal = total + step;
      setTotal(newTotal);

      onQuantityUpdate('add', cost * step);
      onUnitAmountChange(newTotal);
    },
    [cost, onQuantityUpdate, onUnitAmountChange, step, total],
  );

  const handleDecreaseTotal = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();

      const newTotal = total - step;
      if (newTotal < 0) return false;
      setTotal(newTotal);

      onQuantityUpdate('subtract', cost * step);
      onUnitAmountChange(newTotal);
    },
    [cost, onQuantityUpdate, onUnitAmountChange, step, total],
  );

  return (
    <div className='flex flex-col items-center text-center'>
      <div className='leading-0 relative mb-4 w-32 lg:w-36'>
        <Image alt={title} className='h-auto max-w-full rounded-full' height={152} src={image} width={152} />
      </div>
      <h4 className='title-sm mb-2'>{title}</h4>
      <div className='text-muted-foreground mb-4'>
        {formatCurrency(cost)} {unitDescription}
      </div>
      <div className='text-blue mt-auto flex items-center justify-between'>
        <button
          className='border-blue focus:outline-hidden active:bg-blue h-8 w-8 appearance-none rounded-full border-2 bg-white text-2xl active:text-white'
          onClick={handleDecreaseTotal}
        >
          -
        </button>
        <span className='font-greycliff-extrabold w-20 text-2xl '>{total}</span>
        <input name='vr' type='hidden' value={total} />
        <button
          className='border-blue focus:outline-hidden active:bg-blue h-8 w-8 appearance-none rounded-full border-2 bg-white text-2xl active:text-white'
          onClick={handleIncreaseTotal}
        >
          +
        </button>
      </div>
    </div>
  );
}
